import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

function PermissionTab({ allPermissions, rolePermissions, setRolePermissions, disabled }) {
    const [openGroups, setOpenGroups] = useState({});

    const toggleGroup = (group) => {
        setOpenGroups(prev => {
            const currentState = prev[group] !== false;
            return {
                ...prev,
                [group]: !currentState
            };
        });
    };

    const handleTogglePermission = (permissionId) => {
        if (disabled) return;

        setRolePermissions(prev => {
            if (prev.includes(permissionId)) {
                let newPerms = prev.filter(p => p !== permissionId);

                // If removing an '.Access' permission, also remove all dependent actions for this module
                if (permissionId.endsWith('.Access')) {
                    const modulePrefix = permissionId.replace('.Access', '.');
                    newPerms = newPerms.filter(p => !p.startsWith(modulePrefix));
                }

                return newPerms;
            } else {
                let newPerms = [...prev, permissionId];

                // If adding an action (e.g., .View), auto-grant the '.Access' permission if it exists
                if (!permissionId.endsWith('.Access') && permissionId.includes('.')) {
                    const modulePrefix = permissionId.substring(0, permissionId.lastIndexOf('.'));
                    const accessPermId = `${modulePrefix}.Access`;
                    
                    if (!prev.includes(accessPermId) && allPermissions.includes(accessPermId)) {
                        newPerms.push(accessPermId);
                    }
                }

                return newPerms;
            }
        });
    };

    const handleToggleGroup = (groupPerms, isChecked) => {
        if (disabled) return;

        const permIds = groupPerms.map(p => p.id);
        setRolePermissions(prev => {
            if (isChecked) {
                // Add all permissions in group that aren't already included
                const newPerms = permIds.filter(id => !prev.includes(id));
                return [...prev, ...newPerms];
            } else {
                // Remove all permissions in group
                return prev.filter(p => !permIds.includes(p));
            }
        });
    };

    const groupedPermissions = useMemo(() => {
        const getGroupPath = (rawModule) => {
            const map = {
                'DashboardAnalytics': ['Dashboard', 'Analytics'],
                'DashboardMetrics': ['Dashboard', 'Metrics'],
                'UserRole': ['User Management', 'User Role'],
                // 'SecuritySettings': ['User Management', 'Security Settings'],
                'FileManager': ['File Management', 'Image Gallery'],
                'AssetsImage': ['File Management', 'Assets Image'],
                'FileImageUrl': ['File Management', 'Image Url'],
                'PaymentInformation': ['Payment & Subscription', 'Payment Information'],
                'StripePayments': ['Payment & Subscription', 'Stripe Payments'],
                'LandingPageAboutUs': ['Landing Page', 'About Us'],
                'LandingPageOurService': ['Landing Page', 'Our Service'],
                'LandingPagePrivateBlogPost': ['Landing Page', 'Private Blog Post'],
                'LandingPageRoute': ['Landing Page', 'Route'],
                'LandingPageArticle': ['Landing Page', 'Article'],
                'LandingPageTerms': ['Landing Page', 'Terms'],
                'LandingPageHome': ['Landing Page', 'Home'],
                'Appointment': ['System Management', 'General', 'Appointment'],
                'ApplicationParam': ['System Management', 'General', 'Application Parameter'],
                'SupportTicket': ['AI Chatbot Page', 'Support Ticket History'],
                'Notification': ['System Management', 'General', 'Notification'],
                'SystemJob': ['System Management', 'General', 'System Jobs'],
                // 'SystemSettings': ['System Management', 'General', 'System Settings'],
                'DatabaseManagement': ['System Management', 'Database', 'Database Management'],
                'EmailTemplate': ['System Management', 'Email & Messaging', 'Email Template'],
                'SmtpSettings': ['System Management', 'Email & Messaging', 'SMTP Settings'],
                'Mailjet': ['System Management', 'Email & Messaging', 'Mailjet'],
                'NotiInAppTemplate': ['System Management', 'Email & Messaging', 'In-App Notification Template'],
                'AuditLog': ['System Management', 'Logs Management', 'Audit Log'],
                'SystemException': ['System Management', 'Logs Management', 'System Exception'],
                'UserVisitor': ['User Management', 'User Visitor'],
                'UserInformation': ['User Management', 'User Information'],
                'UserPlanSubscription': ['Payment & Subscription', 'User Plan Subscription'],
                'ServiceManager': ['Payment & Subscription', 'Service Manager'],
                'UserCardManagement': ['Payment & Subscription', 'User Card Management'],
                'ChatbotManagement': ['AI Chatbot Page', 'Chatbot Management'],
                'ChatbotFeedback': ['AI Chatbot Page', 'Chat Feedback'],
                'AwsChatbot': ['AI Chatbot Page', 'Manage Chat Sessions'],
                'QdrantSync': ['AI Chatbot Page', 'Qdrant Vector Sync'],
                'AiServicesUsage': ['System Management', 'AI Services', 'Usage'],
                'AiServicesPricing': ['System Management', 'AI Services', 'OpenAI Pricing'],
                'AiServicesMasterData': ['System Management', 'AI Services', 'Master Data'],
                'AiServicesTrafficAnalytics': ['System Management', 'AI Services', 'Traffic Analytics'],
                'ApiMonitoring': ['System Management', 'API Monitoring & Testing'],
                'HeaderDatabaseManagement': ['Header', 'Database Management'],
                'HeaderSystemServicesTesting': ['Header', 'System Services Testing'],
                'HeaderSecuritySettings': ['Header', 'Security Settings'],
                'HeaderSearchSettings': ['Header', 'Search Settings'],
                'HeaderCreateUserAccount': ['Header', 'Create User Account'],
                'SecuritySettings': ['Header', 'Security Settings']
            };
            if (map[rawModule]) return map[rawModule];
            
            if (rawModule.startsWith('LandingPage')) return ['Landing Page', rawModule.replace('LandingPage', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Dashboard')) return ['Dashboard', rawModule.replace('Dashboard', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Aws')) return ['AWS', rawModule.replace('Aws', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Chatbot')) return ['Chatbot', rawModule.replace('Chatbot', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            
            return ['Other', rawModule.replace(/([a-z])([A-Z])/g, '$1 $2')];
        };

        const root = {};
        allPermissions
            .filter(p => !p.includes('Header.CreateAdminAccount') && !p.includes('Header.RoleManagement') && !p.includes('SystemSettings'))
            .forEach(p => {
            const parts = p.split('.');
            let rawModule = parts.length > 1 ? parts[1] : 'Other';
            if (rawModule === 'Header' && parts.length > 2) {
                rawModule = 'Header' + parts[2];
            }
            
            const path = getGroupPath(rawModule);
            const leafNodeName = path[path.length - 1];

            let displayAction = '';
            if (parts.length > 2) {
                let rawAction = parts[2].replace(/([a-z])([A-Z])/g, '$1 $2');
                if (rawModule === 'FileManager' && rawAction === 'Upload') {
                    rawAction = 'Upload & Rename';
                }
                if (rawModule === 'FileManager' && rawAction === 'Delete') {
                    rawAction = 'Delete & Bulk Delete';
                }
                if (rawModule === 'UserPlanSubscription' && rawAction === 'Assign Plan') {
                    rawAction = 'Assign Plan & Assign Specific User';
                }
                if (['LandingPageArticle', 'LandingPageFeatureApp', 'LandingPagePrivateBlogPost'].includes(rawModule) && rawAction === 'Create') {
                    rawAction = 'Add New';
                }
                if (['LandingPageAboutUs', 'LandingPageHome', 'LandingPageTerms'].includes(rawModule) && rawAction === 'Create') {
                    rawAction = 'Add New & Edit';
                }
                if (rawModule === 'Appointment' && rawAction === 'Create') {
                    rawAction = 'Add New';
                }
                if (rawModule === 'SystemJob') {
                    if (rawAction === 'View') rawAction = 'View Detail';
                    if (rawAction === 'Edit') rawAction = 'Enable/Disable';
                }
                if (rawModule === 'SmtpSettings' && rawAction === 'Edit') {
                    rawAction = 'Save Configuration & Test Connection';
                }
                if (rawModule === 'AiServicesMasterData' && rawAction === 'Create') {
                    rawAction = 'Add New & Edit';
                }
                if (rawModule === 'AuditLog' && rawAction === 'Delete') {
                    rawAction = 'Delete (Filter Data & Selected)';
                }
                if (rawModule === 'SystemException' && rawAction === 'Delete') {
                    rawAction = 'Delete Selected';
                }
                if (rawModule === 'ChatbotManagement' && rawAction === 'Create') {
                    rawAction = 'Add New';
                }
                if (rawModule === 'QdrantSync' && rawAction === 'Compare') {
                    rawAction = 'Compare Data';
                }
                displayAction = `${rawAction} ${leafNodeName}`;
            } else {
                displayAction = `Manage ${leafNodeName}`;
            }
            
            let current = root;
            for (let i = 0; i < path.length; i++) {
                const nodeName = path[i];
                if (i === path.length - 1) {
                    if (!current[nodeName]) current[nodeName] = { _isTab: true, perms: [] };
                    current[nodeName].perms.push({ id: p, action: displayAction });
                } else {
                    if (!current[nodeName]) current[nodeName] = {};
                    current = current[nodeName];
                }
            }
        });
        
        return root;
    }, [allPermissions]);

    const renderNode = (nodeName, nodeContent, pathPath, level = 0) => {
        if (nodeContent._isTab) {
            const perms = nodeContent.perms;
            const accessPerm = perms.find(p => p.id.endsWith('.Access'));
            const actionPerms = perms.filter(p => !p.id.endsWith('.Access'));
            
            const allChecked = perms.every(p => rolePermissions.includes(p.id));
            const someChecked = perms.some(p => rolePermissions.includes(p.id));
            const isIndeterminate = someChecked && !allChecked;
            const isOpen = openGroups[pathPath] !== false;
            
            return (
                <div className="tree-group mb-2" key={pathPath} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                    <div className="d-flex align-items-center" style={{ padding: '4px 0' }}>
                        <div 
                            onClick={() => toggleGroup(pathPath)} 
                            style={{ cursor: 'pointer', width: '20px', display: 'flex', justifyContent: 'center' }}
                        >
                            <FontAwesomeIcon 
                                icon={isOpen ? faChevronDown : faChevronRight} 
                                className="text-muted" 
                                style={{ fontSize: '0.85rem' }} 
                            />
                        </div>
                        <div className="form-check m-0 ms-1 d-flex align-items-center">
                            <input 
                                className="form-check-input mt-0 me-2 shadow-sm" 
                                type="checkbox" 
                                id={`group-${pathPath}`}
                                checked={allChecked}
                                ref={input => {
                                    if (input) input.indeterminate = isIndeterminate;
                                }}
                                onChange={(e) => handleToggleGroup(perms, e.target.checked)}
                                disabled={disabled}
                                style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                            />
                            <label 
                                className="form-check-label fw-bold" 
                                htmlFor={`group-${pathPath}`} 
                                style={{ cursor: disabled ? 'not-allowed' : 'pointer', userSelect: 'none', fontSize: '0.9rem', color: disabled ? '#999' : '#2f5aa2' }}
                            >
                                {nodeName}
                            </label>
                        </div>
                    </div>
                    
                    {isOpen && (
                        <div className="tree-children ms-4 mt-2 mb-3">
                            {accessPerm && (
                                <div className="form-check mb-2 pb-2" style={{ borderBottom: '1px dashed #e8eaed' }}>
                                    <input 
                                        className="form-check-input shadow-sm mt-0" 
                                        type="checkbox" 
                                        id={accessPerm.id}
                                        checked={rolePermissions.includes(accessPerm.id)}
                                        onChange={() => handleTogglePermission(accessPerm.id)}
                                        disabled={disabled}
                                        style={{ cursor: disabled ? 'not-allowed' : 'pointer', transform: 'translateY(2px)' }}
                                    />
                                    <label 
                                        className="form-check-label fs-9 ms-2 fw-bold text-primary" 
                                        htmlFor={accessPerm.id} 
                                        style={{ cursor: disabled ? 'not-allowed' : 'pointer', userSelect: 'none', color: disabled ? '#999' : '' }}
                                    >
                                        Enable Screen Access (Show in Menu{nodeName === 'Image Gallery' ? ', Download, Copy Link' : ''})
                                    </label>
                                </div>
                            )}

                            {actionPerms.length > 0 && (
                                <div className="d-flex flex-column gap-1 ms-2">
                                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>AVAILABLE ACTIONS</div>
                                    {actionPerms.map(p => {
                                        const isAccessGranted = accessPerm ? rolePermissions.includes(accessPerm.id) : true;
                                        const isDisabledAction = disabled || !isAccessGranted;
                                        return (
                                        <div className="form-check m-0" key={p.id}>
                                            <input 
                                                className="form-check-input shadow-sm mt-0" 
                                                type="checkbox" 
                                                id={p.id}
                                                checked={rolePermissions.includes(p.id)}
                                                onChange={() => handleTogglePermission(p.id)}
                                                disabled={isDisabledAction}
                                                style={{ cursor: isDisabledAction ? 'not-allowed' : 'pointer', transform: 'translateY(2px)' }}
                                            />
                                            <label 
                                                className={`form-check-label fs-9 ms-2 ${!isDisabledAction ? 'text-body-emphasis' : 'text-muted'}`} 
                                                htmlFor={p.id} 
                                                style={{ cursor: isDisabledAction ? 'not-allowed' : 'pointer', userSelect: 'none' }}
                                            >
                                                {p.action}
                                            </label>
                                        </div>
                                    )})}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        const keys = Object.keys(nodeContent).filter(k => k !== '_isTab' && k !== 'perms');
        if (keys.length === 0) return null;

        const isOpen = openGroups[pathPath] !== false;

        return (
            <div className="tree-node mb-2" key={pathPath} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                <div className="d-flex align-items-center" style={{ padding: '4px 0' }}>
                    <div 
                        onClick={() => toggleGroup(pathPath)} 
                        style={{ cursor: 'pointer', width: '20px', display: 'flex', justifyContent: 'center' }}
                    >
                        <FontAwesomeIcon 
                            icon={isOpen ? faChevronDown : faChevronRight} 
                            className="text-muted" 
                            style={{ fontSize: '0.85rem' }} 
                        />
                    </div>
                    <div className="fw-bold fs-9 ms-1" style={{ color: '#16191f' }}>
                        {nodeName}
                    </div>
                </div>
                {isOpen && (
                    <div className="tree-children ms-4 mt-2" style={{ borderLeft: '1px solid #e8eaed', paddingLeft: '12px' }}>
                        {keys.map(k => renderNode(k, nodeContent[k], `${pathPath}.${k}`, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    if (allPermissions.length === 0) {
        return <div className="text-muted p-3">No permissions available.</div>;
    }

    const rootKeys = Object.keys(groupedPermissions);
    const half = Math.ceil(rootKeys.length / 2);
    const leftKeys = rootKeys.slice(0, half);
    const rightKeys = rootKeys.slice(half);

    const renderColumn = (keys) => (
        <div className="d-flex flex-column gap-1">
            {keys.map(k => renderNode(k, groupedPermissions[k], k))}
        </div>
    );

    const content = (
        <div className="row px-2 m-0 pb-3">
            <div className="col-md-6 border-end pe-4">
                {renderColumn(leftKeys)}
            </div>
            <div className="col-md-6 ps-4">
                {renderColumn(rightKeys)}
            </div>
        </div>
    );

    if (disabled) {
        return (
            <div className="position-relative h-100">
                <div className="position-absolute w-100 h-100 d-flex flex-column align-items-center justify-content-center" style={{ zIndex: 10, background: 'rgba(255,255,255,0.85)', borderRadius: '8px' }}>
                    <div className="badge bg-danger mb-2 p-2 px-3 fs-6 rounded-pill shadow-sm">Super Admin</div>
                    <div className="fw-bold text-dark">This user has full access to all permissions.</div>
                </div>
                <div style={{ opacity: 0.3, pointerEvents: 'none' }}>
                    {content}
                </div>
            </div>
        );
    }

    return content;
}

export default PermissionTab;
